import {useMemo} from 'react';

/**
 * Custom hook for cashflow calculations
 * Handles Excel-like formula logic for interest and principal calculations
 * 
 * @param {Array} rawData - Data from API
 * @param {Object} settings - User settings (interestRate, loanTerm, startingCapital)
 * @param {Object} overrides - User overrides for sales/expenses by month
 * @returns {Object} - Calculated rows and totals
 */
export const useCashflowCalculations = (rawData = [], settings = {}, overrides = {}) => {
  const {
    startingCapital = 0,
    monthlyExpenses = 0
  } = settings;

  const calculations = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return {rows: [], totals: {}};
    }

    // Sort data by month
    const sortedData = [...rawData].sort((a, b) => 
      new Date(a.month) - new Date(b.month)
    );

    let cumulative = startingCapital;
    const now = new Date();

    const rows = sortedData.map((row, index) => {
      const monthKey = row.month;
      const monthDate = new Date(row.month);
      const isCurrentOrPast = monthDate <= now;
      
      // Apply user overrides if any
      const sales = overrides[monthKey]?.sales ?? row.sales;
      const expenses = overrides[monthKey]?.expenses ?? -monthlyExpenses;
      
      // Use actual data for past months, forecast for future
      const income = overrides[monthKey]?.income ?? row.total_income ?? 0;
      const principal = row.total_principal || 0;
      const interest = row.total_interest || 0;

      // Monthly flow calculation
      let monthlyFlow;

      // Excel formula: Cashflow = Previous - Sales + Income + Expenses
      monthlyFlow = -sales + income + expenses;
      cumulative = cumulative + monthlyFlow;

      return {
        ...row,
        month: monthKey,
        sales,
        expenses,
        income,
        principal,
        interest,
        monthly_flow: Math.round(monthlyFlow * 100) / 100,
        cumulative: Math.round(cumulative * 100) / 100,
        is_current_or_past: isCurrentOrPast,
        is_editable: true
      };
    });

    // Calculate totals
    const totals = rows.reduce((acc, row, index) => {
      
      return {
        sales: acc.sales + row.sales,
        income: acc.income + row.income,
        principal: acc.principal + row.principal,
        interest: acc.interest + row.interest,
        expenses: acc.expenses + row.expenses,
        loans_count: acc.loans_count + (row.loans_count || 0),
        paid_count: acc.paid_count + (row.paid_count || 0),
        waiting_count: acc.waiting_count + (row.waiting_count || 0)
      };
    }, {
      sales: 0,
      income: 0,
      principal: 0,
      interest: 0,
      expenses: 0,
      loans_count: 0,
      paid_count: 0,
      waiting_count: 0
    });

    // Add final cumulative to totals
    totals.cumulative = rows.length > 0 ? rows[rows.length - 1].cumulative : startingCapital;

    return {rows, totals};
  }, [rawData, settings, overrides, startingCapital, monthlyExpenses]);

  return calculations;
};

export default useCashflowCalculations;